/**
 * WEBSOCKET / SIGNALR FAN SERVICE: MEMORAMA (MEMORY 1 VS 1)
 * Wrapper de comunicación en tiempo real para el Aficionado Móvil / Participante de Memorama.
 * Gestiona la conexión persistente con SignalR Hub (`/hub/entertainment`) para recibir la cuenta regresiva sincronizada,
 * fase de memorización previa, volteo de cartas en vivo con Zero-Knowledge, telemetría a 100ms, aciertos/fallos,
 * pantalla de fin de partida y el evento prioritario de desalojo 'kicked_out'.
 * 
 * @module utilities/WS/MemoryFanWS
 */

import SignalRService from './SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes generales.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el dispositivo móvil del aficionado al Hub de SignalR con el rol 'memory:fan'.
 * Soporta polimórficamente el orden de parámetros tanto de Interaktia-V2 como variantes heredadas.
 * 
 * Eventos procesados:
 * - `kicked_out`: Desalojo forzoso si otro dispositivo en la sala selecciona el mismo personaje.
 * - `memory_countdown`: Conteo regresivo oficial 3..2..1.
 * - `memory_preview` / `memory_preview_start`: Fase de memorización de cartas descubiertas.
 * - `memory_preview_ended`: Fin de memorización y bloqueo/desbloqueo de cartas.
 * - `memory_playing_started` / `memory_start` / `memory_game_start`: Inicio de la partida activa.
 * - `memory_card_flipped`: Carta volteada por el jugador o rival.
 * - `memory_match_success` / `match_success`: Coincidencia y acierto de par.
 * - `memory_match_failed` / `match_failed`: Error de coincidencia (desencadena volteo boca abajo tras delay).
 * - `memory_cards_unflipped` / `cards_unflipped`: Cartas devueltas boca abajo.
 * - `memory_telemetry`: Telemetría rápida a 100ms (cronómetro restante, puntajes, flips y estados de jugadores).
 * - `memory_all_pairs_matched` / `all_pairs_matched`: Todos los pares completados por un jugador.
 * - `memory_finished` / `memory_game_over` / `memory_game_finished`: Fin de la partida y pantalla de ganador/empate.
 * - `memory_sync_state` / `memory_state` / `memory_cover_shown` / `memory_game_ready`: Sincronización general de estado.
 * - `memory_score_adjusted`: Puntaje ajustado manualmente.
 * - `memory_names_updated`: Nombres de jugadores actualizados.
 * - `memory_match_reset`: Partida reiniciada a 'game_ready'.
 * - `memory_player_left` / `leave_game`: Notificación de abandono.
 * 
 * @param {Function|string} [param1] - Callback onIncomingMessage o playerChoice ("player1" | "player2").
 * @param {Function} [param2] - Callback onStatusChange o onIncomingMessage.
 * @param {string|Function} [param3] - Identificador GUID roomId o callback onStatusChange.
 * @param {string|Object} [param4] - playerChoice ("player1" | "player2") o roomId o eventCallbacks.
 * @param {Object} [param5={}] - Callbacks específicos por tipo de evento.
 */
export const ConnectWebSocketForMemoryFan = (
    param1,
    param2,
    param3,
    param4,
    param5
) => {
    let onIncomingMessage = () => {};
    let onStatusChange = () => {};
    let roomId = '';
    let playerChoice = '';
    let eventCallbacks = {};

    if (typeof param1 === 'function') {
        // Formato estándar Interaktia V2: (onIncomingMessage, onStatusChange, roomId, playerChoice, eventCallbacks)
        onIncomingMessage = param1;
        onStatusChange = typeof param2 === 'function' ? param2 : () => {};
        roomId = typeof param3 === 'string' ? param3 : '';
        playerChoice = typeof param4 === 'string' ? param4 : '';
        eventCallbacks = (typeof param5 === 'object' && param5 !== null) ? param5 : {};
    } else {
        // Formato polimórfico: (playerChoice, onIncomingMessage, onStatusChange, roomId, eventCallbacks)
        playerChoice = typeof param1 === 'string' ? param1 : '';
        onIncomingMessage = typeof param2 === 'function' ? param2 : () => {};
        onStatusChange = typeof param3 === 'function' ? param3 : () => {};
        roomId = typeof param4 === 'string' ? param4 : '';
        eventCallbacks = (typeof param5 === 'object' && param5 !== null) ? param5 : {};
    }

    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala / roomId
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // 1. FILTRADO POR SALA
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.screenId
            || messageEnvelope.ScreenId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.payload?.screenId
            || messageEnvelope.payload?.ScreenId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId
            || messageEnvelope.data?.screenId;

        if (roomId && envelopeRoomId !== undefined && envelopeRoomId !== null) {
            if (String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
                return;
            }
        }

        const rawType = String(messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();
        const payloadData = messageEnvelope.payload !== undefined ? messageEnvelope.payload : messageEnvelope.data;

        // 2. MANEJO PRIORITARIO DEL EVENTO KICKED_OUT (DESALOJO)
        if (rawType === 'kicked_out') {
            console.warn('[MemoryFanWS] Evento kicked_out recibido para esta sesión:', messageEnvelope);

            if (typeof eventCallbacks.onKickedOut === 'function') {
                try {
                    eventCallbacks.onKickedOut(payloadData, messageEnvelope);
                } catch (kickErr) {
                    console.error('[MemoryFanWS] Error en callback onKickedOut:', kickErr);
                }
            }
        }

        // 3. DESPACHO A CALLBACKS ESPECÍFICOS SEGÚN TIPO DE EVENTO
        switch (rawType) {
            // Conteo regresivo
            case 'memory_countdown':
                if (typeof eventCallbacks.onCountdown === 'function') {
                    try {
                        eventCallbacks.onCountdown(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onCountdown:', err);
                    }
                }
                break;

            // Fase de memorización (Preview)
            case 'memory_preview':
            case 'memory_preview_start':
                if (typeof eventCallbacks.onPreview === 'function') {
                    try {
                        eventCallbacks.onPreview(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onPreview:', err);
                    }
                }
                break;

            case 'memory_preview_ended':
                if (typeof eventCallbacks.onPreviewEnded === 'function') {
                    try {
                        eventCallbacks.onPreviewEnded(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onPreviewEnded:', err);
                    }
                }
                break;

            // Inicio de partida activa
            case 'memory_playing_started':
            case 'memory_start':
            case 'memory_game_start':
                if (typeof eventCallbacks.onGameStart === 'function') {
                    try {
                        eventCallbacks.onGameStart(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onGameStart:', err);
                    }
                }
                if (typeof eventCallbacks.onPlayingStarted === 'function') {
                    try {
                        eventCallbacks.onPlayingStarted(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onPlayingStarted:', err);
                    }
                }
                break;

            // Volteo de carta
            case 'memory_card_flipped':
                if (typeof eventCallbacks.onCardFlipped === 'function') {
                    try {
                        eventCallbacks.onCardFlipped(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onCardFlipped:', err);
                    }
                }
                break;

            // Acierto de par (Match Success)
            case 'memory_match_success':
            case 'match_success':
                if (typeof eventCallbacks.onMatchSuccess === 'function') {
                    try {
                        eventCallbacks.onMatchSuccess(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onMatchSuccess:', err);
                    }
                }
                break;

            // Fallo de par (Match Failed)
            case 'memory_match_failed':
            case 'match_failed':
                if (typeof eventCallbacks.onMatchFailed === 'function') {
                    try {
                        eventCallbacks.onMatchFailed(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onMatchFailed:', err);
                    }
                }
                break;

            // Cartas vueltas a tapar tras fallo
            case 'memory_cards_unflipped':
            case 'cards_unflipped':
                if (typeof eventCallbacks.onCardsUnflipped === 'function') {
                    try {
                        eventCallbacks.onCardsUnflipped(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onCardsUnflipped:', err);
                    }
                }
                break;

            // Telemetría rápida a 100ms
            case 'memory_telemetry':
                if (typeof eventCallbacks.onTelemetry === 'function') {
                    try {
                        eventCallbacks.onTelemetry(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onTelemetry:', err);
                    }
                }
                break;

            // Todos los pares completados
            case 'memory_all_pairs_matched':
            case 'all_pairs_matched':
                if (typeof eventCallbacks.onAllPairsMatched === 'function') {
                    try {
                        eventCallbacks.onAllPairsMatched(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onAllPairsMatched:', err);
                    }
                }
                break;

            // Fin de la partida
            case 'memory_finished':
            case 'memory_game_over':
            case 'memory_game_finished':
                if (typeof eventCallbacks.onFinished === 'function') {
                    try {
                        eventCallbacks.onFinished(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onFinished:', err);
                    }
                }
                if (typeof eventCallbacks.onGameOver === 'function') {
                    try {
                        eventCallbacks.onGameOver(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onGameOver:', err);
                    }
                }
                break;

            // Sincronización de estado general
            case 'memory_sync_state':
            case 'memory_state':
            case 'memory_cover_shown':
            case 'memory_cover':
            case 'memory_game_ready':
            case 'memory_game_shown':
                if (typeof eventCallbacks.onSyncState === 'function') {
                    try {
                        eventCallbacks.onSyncState(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onSyncState:', err);
                    }
                }
                break;

            // Puntaje ajustado
            case 'memory_score_adjusted':
                if (typeof eventCallbacks.onScoreAdjusted === 'function') {
                    try {
                        eventCallbacks.onScoreAdjusted(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onScoreAdjusted:', err);
                    }
                }
                break;

            // Nombres actualizados
            case 'memory_names_updated':
            case 'memory_update_names':
                if (typeof eventCallbacks.onNamesUpdated === 'function') {
                    try {
                        eventCallbacks.onNamesUpdated(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onNamesUpdated:', err);
                    }
                }
                break;

            // Partida reiniciada
            case 'memory_match_reset':
                if (typeof eventCallbacks.onMatchReset === 'function') {
                    try {
                        eventCallbacks.onMatchReset(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onMatchReset:', err);
                    }
                }
                break;

            // Jugador abandonó la sala
            case 'memory_player_left':
            case 'leave_game':
                if (typeof eventCallbacks.onPlayerLeft === 'function') {
                    try {
                        eventCallbacks.onPlayerLeft(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryFanWS] Error en callback onPlayerLeft:', err);
                    }
                }
                break;

            default:
                break;
        }

        // 4. DESPACHAR AL HANDLER GENERAL
        if (typeof onIncomingMessage === 'function') {
            try {
                onIncomingMessage(messageEnvelope);
            } catch (err) {
                console.error('[MemoryFanWS] Error en onIncomingMessage general:', err);
            }
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'memory:fan',
        roomId: roomId ? String(roomId) : '',
        playerChoice: playerChoice ? String(playerChoice) : ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[MemoryFanWS] Error al conectar aficionado con SignalR Hub:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del aficionado móvil y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForMemoryFan = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[MemoryFanWS] Error al desconectar la sesión SignalR del aficionado:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForMemoryFan,
    DisconnectWebSocketForMemoryFan
};
